import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Ticket, UserPlus } from "lucide-react";

/**
 * CommunityRecentJoins — small ticker of the most recent members. Shows
 * an invite-code chip when a member joined via an invite link, so owners
 * can attribute growth to specific codes. Hides itself when there's only
 * one member (the owner) so brand-new communities don't render an empty
 * strip.
 */
export default function CommunityRecentJoins({ communityId }) {
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/communities/${communityId}/recent-joins?limit=6`);
      setRows(data.rows || []);
    } catch { /* silent */ }
  }, [communityId]);

  useEffect(() => {
    load();
    // Refresh so an invite redemption pushed by another tab shows up
    // without a manual reload.
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, [load]);

  const recent = rows.filter((r) => r.role !== "owner");
  if (recent.length === 0) return null;

  return (
    <section className="mt-6" data-testid="community-recent-joins">
      <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-2 flex items-center gap-1.5">
        <UserPlus size={10} /> Recent joins
      </div>
      <ul className="flex flex-wrap gap-2">
        {recent.map((r) => (
          <li
            key={r.id}
            data-testid={`community-recent-joins-row-${r.user?.id}`}
            className="flex items-center gap-2 bg-zinc-950/60 border border-white/10 rounded-full pl-1 pr-3 py-1"
          >
            <Link to={`/u/${r.user?.id}`}>
              <Avatar className="w-6 h-6">
                <AvatarImage src={r.user?.avatar_url} />
                <AvatarFallback className="bg-rose-600 text-white text-[10px]">
                  {r.user?.display_name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Link>
            <div className="text-[11px] leading-tight">
              <div className="text-white truncate max-w-[9rem]">
                {r.user?.display_name}
              </div>
              <div className="text-zinc-500 text-[10px] flex items-center gap-1">
                {r.via_invite_code && (
                  <span
                    className="inline-flex items-center gap-0.5 text-amber-300"
                    title={`Via invite ${r.via_invite_code}`}
                  >
                    <Ticket size={9} /> invite
                  </span>
                )}
                <span>{relTime(r.created_at)}</span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
